import type { DeploymentPullRequest } from '@dev-console/types';
import { pluralize } from '@shared/strings/utils';
import { useState } from 'react';

const COMPACT_VISIBLE_PR_LIMIT = 3;
const GITHUB_COMMIT_URL = 'https://github.com/long-view-labs/mono-repo/commit';
const SHORT_SHA_LENGTH = 7;

export function DeploymentPullRequests({
  pullRequests,
  compact = false,
  deploymentCommitSha,
}: {
  pullRequests: DeploymentPullRequest[];
  compact?: boolean;
  deploymentCommitSha?: string;
}) {
  const [expanded, setExpanded] = useState(false);

  if (pullRequests.length === 0) return null;

  if (compact) {
    const overflowCount = Math.max(0, pullRequests.length - COMPACT_VISIBLE_PR_LIMIT);
    const visible = overflowCount > 0 && !expanded ? pullRequests.slice(0, COMPACT_VISIBLE_PR_LIMIT) : pullRequests;

    return (
      <div className='min-w-0 flex-1'>
        <ul className='min-w-0 space-y-1'>
          {visible.map((pullRequest) => (
            <li key={pullRequest.number} className='min-w-0'>
              <PullRequestLine
                pullRequest={pullRequest}
                commitSha={pullRequestCommitSha(pullRequest, pullRequests, deploymentCommitSha)}
              />
            </li>
          ))}
        </ul>
        {overflowCount > 0 && (
          <button
            type='button'
            aria-expanded={expanded}
            aria-label={
              expanded
                ? 'Hide additional pull requests'
                : `Show ${overflowCount} more ${pluralize(overflowCount, 'pull request')}`
            }
            className='mt-1 rounded-md px-1 py-0.5 text-[11px] font-medium leading-none text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setExpanded((current) => !current);
            }}
          >
            {expanded ? 'Hide extra PRs' : `+${overflowCount} more ${pluralize(overflowCount, 'PR')}`}
          </button>
        )}
      </div>
    );
  }

  return (
    <ul className='min-w-0 space-y-2'>
      {pullRequests.map((pullRequest) => (
        <li key={pullRequest.number} className='min-w-0'>
          <PullRequestLine pullRequest={pullRequest} commitSha={null} />
        </li>
      ))}
    </ul>
  );
}

// Deployments recorded before per-PR SHAs were captured only know the deploy's head commit, which is
// only the right commit to show when the deploy shipped a single PR.
function pullRequestCommitSha(
  pullRequest: DeploymentPullRequest,
  pullRequests: DeploymentPullRequest[],
  deploymentCommitSha: string | undefined
): string | null {
  if (pullRequest.commitSha != null) return pullRequest.commitSha;
  return pullRequests.length === 1 ? deploymentCommitSha ?? null : null;
}

function PullRequestLine({ pullRequest, commitSha }: { pullRequest: DeploymentPullRequest; commitSha: string | null }) {
  const title = `#${pullRequest.number} ${pullRequest.title}`;

  return (
    <div className='flex min-w-0 items-center gap-1.5'>
      {commitSha != null && (
        <>
          <a
            href={`${GITHUB_COMMIT_URL}/${commitSha}`}
            target='_blank'
            rel='noopener noreferrer'
            className='shrink-0 font-mono text-sm text-blue-700 hover:underline'
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              window.open(`${GITHUB_COMMIT_URL}/${commitSha}`, '_blank', 'noopener,noreferrer');
            }}
          >
            {commitSha.slice(0, SHORT_SHA_LENGTH)}
          </a>
          <Separator />
        </>
      )}
      <a
        href={pullRequest.url}
        target='_blank'
        rel='noopener noreferrer'
        className='min-w-0 truncate text-sm font-medium text-blue-700 hover:underline'
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          window.open(pullRequest.url, '_blank', 'noopener,noreferrer');
        }}
      >
        {title}
      </a>
      <Separator />
      <span className='shrink-0 text-sm text-neutral-500'>@{pullRequest.authorLogin}</span>
    </div>
  );
}

function Separator() {
  return (
    <span className='shrink-0 text-neutral-300' aria-hidden='true'>
      {' | '}
    </span>
  );
}
